'use client';

import { createContext, useContext, useMemo, useState, useSyncExternalStore } from 'react';
import type { PropsWithChildren } from 'react';

// EWM-521 / EWM-612 — scoped mutation tracker. Tracks in-flight server writes keyed by
// a feature SCOPE ('cart', 'account', 'business-unit') so a write only affects what
// cares about that scope — never a site-wide freeze.
//
// Phase 1 (EWM-610) took cart QUANTITY edits off this entirely (optimistic + debounced).
// Phase 2 (EWM-612): the remaining cart writes raise the 'cart' scope, which drives a
// single processing modal (<CartProcessingModal>) instead of disabling individual
// controls — the per-button disable/enable was inconsistent. account / business-unit
// writes use their own scopes so they neither show the cart modal nor freeze anything.
//
// `withLock` raises the scope's counter for the duration of the async call, then
// releases on the next macrotask so the optimistic re-render + dependent recomputes
// (totals, freight, ship-estimate) flush BEFORE the indicator clears — no flicker. A
// per-scope counter keeps the scope raised until the LAST overlapping write settles.

export type MutationScope = 'global' | 'cart' | 'account' | 'business-unit';

export interface MutationLockOptions {
  scope?: MutationScope;
}

export interface MutationLockValue {
  isMutating: boolean;
  withLock: <T>(fn: () => Promise<T>, opts?: MutationLockOptions) => Promise<T>;
}

interface Store {
  subscribe: (listener: () => void) => () => void;
  getCount: (scope: MutationScope) => number;
  withLock: <T>(fn: () => Promise<T>, opts?: MutationLockOptions) => Promise<T>;
}

const MutationLockContext = createContext<Store | null>(null);

const nextMacrotask = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

const createStore = (): Store => {
  const counts = new Map<MutationScope, number>();
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach((l) => l());

  const withLock = async <T,>(fn: () => Promise<T>, opts?: MutationLockOptions): Promise<T> => {
    const scope = opts?.scope ?? 'global';
    counts.set(scope, (counts.get(scope) ?? 0) + 1);
    emit();
    try {
      return await fn();
    } finally {
      // Let the resulting re-render + dependent recomputes flush before releasing,
      // so the indicator doesn't clear on a not-yet-settled cart (EWM-414 flicker guard).
      await nextMacrotask();
      counts.set(scope, Math.max(0, (counts.get(scope) ?? 0) - 1));
      emit();
    }
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getCount: (scope) => counts.get(scope) ?? 0,
    withLock,
  };
};

const MutationLockProvider = ({ children }: PropsWithChildren) => {
  // EWM-760 — react-hooks/refs: lazy ref-init read during render. `useState`'s
  // lazy initializer runs exactly once (mount) and the value never changes
  // thereafter, so this is behavior-identical to the old
  // `if (!storeRef.current) storeRef.current = createStore()` guard.
  const [store] = useState(() => createStore());
  return <MutationLockContext.Provider value={store}>{children}</MutationLockContext.Provider>;
};

// Outside a provider (Storybook, isolated tests) `withLock` is a passthrough and the
// lock reads idle, so consumers never crash or freeze.
const PASSTHROUGH_STORE: Store = {
  subscribe: () => () => {},
  getCount: () => 0,
  withLock: (fn) => fn(),
};

// Subscribe to a single scope (default 'global'). Per-scope subscription means a
// 'cart' write doesn't re-render an 'account' reader and vice-versa.
export const useMutationLock = (scope: MutationScope = 'global'): MutationLockValue => {
  const store = useContext(MutationLockContext) ?? PASSTHROUGH_STORE;
  const count = useSyncExternalStore(
    store.subscribe,
    () => store.getCount(scope),
    () => 0,
  );
  return useMemo(() => ({ isMutating: count > 0, withLock: store.withLock }), [count, store]);
};

export default MutationLockProvider;
